import { inject, Injectable } from '@angular/core';
import { Permission } from '@core/interfaces/i_permission';
import { Supabase } from './supabase';

@Injectable({
  providedIn: 'root',
})
export class Permissions {
  private supabase = inject(Supabase);

  /**
   * Fetches the complete list of permissions for a specific role.
   * This is now a stateless data fetcher to avoid circular dependencies.
   */
  async fetchByRole(role: string): Promise<Permission[]> {
    const { data, error } = await this.supabase.client
      .from('permissions')
      .select('*')
      .contains('roles', [role]);

    if (error) {
      console.error('Error fetching permissions:', error);
      return [];
    }

    return (data || []) as Permission[];
  }

  /**
   * Logic to check if an action is allowed on a resource.
   */
  hasPermission(permissions: Permission[], resource: string, action: string): boolean {
    return permissions.some(p => p.resource === resource && p.action === action);
  }

  /**
   * Logic to check if a specific route is permitted.
   */
  hasPermissionForRoute(permissions: Permission[], route: string): boolean {
    return permissions.some(p => {
      if (p.route === undefined || p.route === null) return false;

      // Normalize routes: convert "" to "/" and ensure they start with /
      const normalizeRoute = (r: string) => {
        if (r === '' || r === '/') return '/';
        return r.startsWith('/') ? r : '/' + r;
      };

      const permRoute = normalizeRoute(p.route);
      const targetRoute = normalizeRoute(route);

      // Exact match for the root route
      if (permRoute === '/' && targetRoute === '/') return true;

      // Check if the target route starts with the permission route
      return targetRoute === permRoute || targetRoute.startsWith(permRoute + '/');
    });
  }

  /**
   * Logic to filter and hierarchy-organize sidebar routes.
   */
  getVisibleRoutes(permissions: Permission[]): Permission[] {
    const visible = permissions.filter(p => p.show_in_sidebar && p.title);

    // Split roots and children
    const roots = visible.filter(p => !p.parent_id);
    const children = visible.filter(p => !!p.parent_id);

    // Map children to parents
    if (children.length > 0) {
      for (const child of children) {
        const parent = roots.find(r => r.id === child.parent_id);
        if (parent) {
          if (!parent.children) parent.children = [];
          parent.children.push(child);
        }
      }
    }

    return roots;
  }
}
